import { DateTime } from 'luxon';
import { parseDateString, toDateString } from '@common/utils/date';
import {
  FleetDataPerformanceChange,
  FleetDataReliabilityWeek,
  FleetDataRobotWeek,
} from 'models/fleet-management/FleetDataSet';
import { rswVersionGroup } from './rsw-version';

/** The reliability trend and the robot detail always look back this many weeks. */
export const TREND_WEEKS = 12;

/**
 * A fixed run of whole weeks ending with the week of `min(end, today)`, whatever window the filter
 * bar holds, so a trend always has the same number of points to compare.
 */
export const trendWindow = (end: string, today: DateTime = DateTime.now()) => {
  const endDay = DateTime.min(parseDateString(end), today.startOf('day'));
  const start = endDay.startOf('week').minus({ weeks: TREND_WEEKS - 1 });
  return { start: toDateString(start), end: toDateString(endDay) };
};

/** Monday of every week from `start` on, as the `yyyy-MM-dd` a week row is matched by. */
export const weekGrid = (start: string, count: number = TREND_WEEKS) => {
  const monday = parseDateString(start).startOf('week');
  return Array.from({ length: count }, (_, index) => toDateString(monday.plus({ weeks: index })));
};

/** The `yyyy-MM-dd` Monday a backend `week_start` falls on. */
export const weekKey = (weekStart: string) =>
  toDateString(DateTime.fromISO(weekStart).startOf('week'));

export interface ReliabilityPoint {
  week: string;
  /** Operating hours per failed mission; null for a week without a failure. */
  mtbf: number | null;
  /** Failed missions as a share of all missions, in percent. */
  failureRatio: number | null;
  /** Successful missions as a share of all missions, in percent. */
  successRate: number | null;
}

/** The three reliability figures for every week of the grid, blank where no mission ran. */
export const reliabilitySeries = (
  missions: FleetDataReliabilityWeek[],
  grid: string[],
): ReliabilityPoint[] => {
  const byWeek = new Map(missions.map((week) => [weekKey(week.week_start), week]));
  return grid.map((week) => {
    const row = byWeek.get(week);
    const missionCount = row?.mission_count ?? 0;
    const failures = row?.failures_count ?? 0;
    return {
      week,
      mtbf: failures > 0 ? (row?.total_duration ?? 0) / failures : null,
      failureRatio: missionCount > 0 ? (failures / missionCount) * 100 : null,
      successRate: missionCount > 0 ? ((row?.successful_count ?? 0) / missionCount) * 100 : null,
    };
  });
};

/** The last value that is not null, and the first, so a card can say where the trend went. */
export const trendEnds = (values: (number | null)[]) => {
  const present = values.filter((value): value is number => value != null);
  return { first: present.at(0) ?? null, last: present.at(-1) ?? null };
};

/** One robot's weeks laid on the grid, a week without missions filled with zeros. */
export const fillRobotWeeks = (rows: FleetDataRobotWeek[], grid: string[]) => {
  const byWeek = new Map(rows.map((row) => [weekKey(row.week_start), row]));
  return grid.map((week) => {
    const row = byWeek.get(week);
    return {
      week,
      cleanedArea: row?.cleaned_area ?? 0,
      cleaningPerformance: row?.cleaning_performance ?? null,
      missionCount: row?.mission_count ?? 0,
      rswVersions: row?.rsw_versions ?? [],
    };
  });
};

/**
 * The weeks an RSW update landed: a week that ran a release (see `rswVersionGroup`) the last week
 * with missions did not, with the release it moved to. A build within the same release, a week
 * without missions, or one that only dropped the old release after an update mid-week, changes
 * nothing.
 */
export const versionChanges = (weeks: { week: string; rswVersions: string[] }[]) => {
  const changes: { week: string; version: string }[] = [];
  let previous: string[] | null = null;
  weeks.forEach(({ week, rswVersions }) => {
    if (rswVersions.length === 0) return;
    const releases = [...new Set(rswVersions.map(rswVersionGroup))];
    const seen = previous;
    const added = seen == null ? [] : releases.filter((release) => !seen.includes(release));
    if (added.length > 0) changes.push({ week, version: added.sort().join(', ') });
    previous = releases;
  });
  return changes;
};

export const median = (values: number[]) => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

export enum PerformanceFilter {
  ALL = 'ALL',
  DECLINING = 'DECLINING',
  NO_BASELINE = 'NO_BASELINE',
}

/** The rows a pill keeps. The server already sorted them worst first, so order is left alone. */
export const filterPerformanceRows = (
  rows: FleetDataPerformanceChange[],
  filter: PerformanceFilter,
) => {
  if (filter === PerformanceFilter.DECLINING) {
    return rows.filter((row) => row.has_baseline && row.ratio != null && row.ratio < 1);
  }
  if (filter === PerformanceFilter.NO_BASELINE) return rows.filter((row) => !row.has_baseline);
  return rows;
};

/** The change against the baseline as a signed share, clamped so one outlier cannot fill the bar. */
export const ratioDeviation = (ratio: number) => Math.max(-1, Math.min(1, ratio - 1));

/** "−35%" or "+12%", with a real minus sign so the column lines up. */
export const formatRatioDelta = (ratio: number) => {
  const percent = Math.round((ratio - 1) * 100);
  return `${percent < 0 ? '−' : '+'}${Math.abs(percent)}%`;
};
